import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import config from '../../config';
import styles from './BulkPhotoUpload.module.css';

interface Recipe {
  id: string;
  title: string;
  source_url?: string;
  created_at: string;
}

interface BulkPhotoUploadProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const BulkPhotoUpload: React.FC<BulkPhotoUploadProps> = ({ isOpen, onClose, onComplete }) => {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadCaption, setUploadCaption] = useState('');
  const [uploadPrimary, setUploadPrimary] = useState(true);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load recipes without photos
  const loadRecipesWithoutPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.API_BASE_URL}/admin/recipes-without-photos?limit=50`);
      setRecipes(response.data);
      setCurrentIndex(0);
    } catch (err) {
      console.error('Failed to load recipes without photos:', err);
      setError('Failed to load recipes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadRecipesWithoutPhotos();
    }
  }, [isOpen, loadRecipesWithoutPhotos]);

  const currentRecipe = recipes[currentIndex];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      setUploadSuccess(false);
      setError(null);
    }
  };

  const uploadPhoto = async () => {
    if (!uploadFile || !currentRecipe) return;

    setUploading(true);
    setError(null);

    try {
      // Use the same upload pattern as the existing Admin UI
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('caption', uploadCaption);
      formData.append('is_primary', String(uploadPrimary));

      await axios.post(
        `${config.API_BASE_URL}/recipes/${currentRecipe.id}/photos/upload`,
        formData,
        { 
          headers: { 
            'Content-Type': 'multipart/form-data' 
          } 
        }
      );

      setUploadSuccess(true);
      
      // Auto-advance to next recipe after a short delay
      setTimeout(() => {
        nextRecipe();
      }, 1500);

    } catch (err) {
      console.error('Upload failed:', err);
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const nextRecipe = () => {
    if (currentIndex < recipes.length - 1) {
      setCurrentIndex(currentIndex + 1);
      resetUploadForm();
    }
  };

  const prevRecipe = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      resetUploadForm();
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadCaption('');
    setUploadPrimary(true);
    setUploadSuccess(false);
    setError(null);
  };

  const skipRecipe = () => {
    nextRecipe();
  };

  const finishUpload = () => {
    onComplete();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>Upload Missing Photos</h2>
          <div className={styles.progress}>
            Recipe {currentIndex + 1} of {recipes.length}
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        {loading ? (
          <div className={styles.loading}>
            <p>Loading recipes without photos...</p>
          </div>
        ) : recipes.length === 0 ? (
          <div className={styles.empty}>
            <p>🎉 All recipes have photos!</p>
            <button className={styles.button} onClick={onClose}>
              Close
            </button>
          </div>
        ) : currentRecipe ? (
          <div className={styles.content}>
            <div className={styles.recipeInfo}>
              <h3>{currentRecipe.title}</h3>
              {currentRecipe.source_url && (
                <p className={styles.sourceUrl}>
                  <a href={currentRecipe.source_url} target="_blank" rel="noreferrer">
                    {currentRecipe.source_url}
                  </a>
                </p>
              )}
            </div>

            <div className={styles.uploadForm}>
              <div className={styles.formGroup}>
                <label htmlFor="photo-upload">Select Photo</label>
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className={styles.fileInput}
                />
                {uploadFile && (
                  <div className={styles.fileInfo}>
                    <p>Selected: {uploadFile.name}</p>
                    <p>Size: {(uploadFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="caption">Caption (optional)</label>
                <input
                  id="caption"
                  type="text"
                  value={uploadCaption}
                  onChange={(e) => setUploadCaption(e.target.value)}
                  placeholder="Enter photo caption..."
                  className={styles.textInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={uploadPrimary}
                    onChange={(e) => setUploadPrimary(e.target.checked)}
                  />
                  Set as primary photo
                </label>
              </div>

              {error && <div className={styles.error}>{error}</div>}
              {uploadSuccess && <div className={styles.success}>Photo uploaded successfully!</div>}

              <div className={styles.actions}>
                <button
                  className={styles.uploadButton}
                  onClick={uploadPhoto}
                  disabled={!uploadFile || uploading}
                >
                  {uploading ? 'Uploading...' : 'Upload Photo'}
                </button>
                <button
                  className={styles.skipButton}
                  onClick={skipRecipe}
                  disabled={uploading}
                >
                  Skip Recipe
                </button>
              </div>
            </div>

            <div className={styles.navigation}>
              <button
                className={styles.navButton}
                onClick={prevRecipe}
                disabled={currentIndex === 0}
              >
                ← Previous
              </button>
              <span className={styles.navInfo}>
                {currentIndex + 1} / {recipes.length}
              </span>
              <button
                className={styles.navButton}
                onClick={nextRecipe}
                disabled={currentIndex === recipes.length - 1}
              >
                Next →
              </button>
            </div>

            <div className={styles.finishActions}>
              <button className={styles.finishButton} onClick={finishUpload}>
                Finish Upload Session
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default BulkPhotoUpload;
